import { AnonymousStudentProfileUpdated, AnonymousStudentServiceReady } from '@studyportals/anonymous-student-interfaces';

import {
	IEventAggregationService,
	ISubscriber,
} from '@studyportals/event-aggregation-service-interface';
import { StudentField } from '@studyportals/studentdomain';
import store from '@/store';
import globals from '@/utils/global';
import DisciplineFacetsRepository from '@/repositories/DisciplineFacetsRepository';
import ResultsRepository from '@/repositories/ResultsRepository';
import IUserData from '@/interfaces/user-data.interface';
import { DataStorage } from '@studyportals/data-storage';
import { CurrencyConversionServiceReadyEvent, LocalizedContentServiceReadyEvent } from '@studyportals/portal-localized-content';
import PathUtils from '@/utils/PathUtils';
import SuggestionsRepository from '@/repositories/SuggestionsRepository';
import { OmniSearchReadyEvent, OmniSearchFiltersClearedEvent, FilterType, OmniSearchFiltersUpdatedEvent } from "@studyportals/omnisearch-interfaces";
import { InlineSearchAppInitialInput, InlineSearchSendEvent } from "@studyportals/inlinesearch-interfaces";
import { EventAggregationService } from "@/utils/EventAggregationService";

export class DependencyCheckerPlugin implements
	ISubscriber<AnonymousStudentServiceReady |
	AnonymousStudentProfileUpdated |
	LocalizedContentServiceReadyEvent |
	CurrencyConversionServiceReadyEvent |
	InlineSearchSendEvent |
	OmniSearchReadyEvent |
	OmniSearchFiltersUpdatedEvent>
{
	private localizedContentServiceIsReady = false;
	private currencyConversionServiceIsReady = false;

	private get eventAggregationService(): IEventAggregationService | null {
		if (typeof window['EventAggregationService'] === 'undefined') {
			return null;
		}
		return window['EventAggregationService'];
	}

	public initialize(): void {
		globals.disciplineFacetsRepository = new DisciplineFacetsRepository();
		globals.resultsRepository = new ResultsRepository();
		globals.suggestionsRepository = new SuggestionsRepository();

		if (!this.eventAggregationService) {
			return;
		}

		this.eventAggregationService.subscribeTo(
			AnonymousStudentServiceReady.EventType,
			this,
			true,
		);


		this.eventAggregationService.subscribeTo(
			AnonymousStudentProfileUpdated.EventType,
			this,
			false,
		);

		this.eventAggregationService.subscribeTo(
			LocalizedContentServiceReadyEvent.EventType,
			this,
			true,
		);

		this.eventAggregationService.subscribeTo(
			CurrencyConversionServiceReadyEvent.EventType,
			this,
			true,
		);

		this.eventAggregationService.subscribeTo(
			InlineSearchSendEvent.EventType,
			this,
			true,
		);

		window.addEventListener('storage', (event) => {
			void (async (): Promise<void> => {
				if (event.key === 'global_access_initiative_questionnaire_taken') {
					const studentData = await globals.anonymousStudent.getStudentData([StudentField.RESIDENCE_COUNTRY_ID]);
					const countryOfResidenceId = studentData[StudentField.RESIDENCE_COUNTRY_ID];

					if (countryOfResidenceId) {
						store.mutations.setUserData({ countryOfResidence: countryOfResidenceId })
						store.mutations.setGAIQuestionnaireTaken(DataStorage.retrieve(event.key) === 'true');
					}
				}
			})();
		});

		if (!PathUtils.isInSearchPage()) return;

		this.eventAggregationService.subscribeTo(
			OmniSearchReadyEvent.EventType,
			this,
			true
		);

		this.eventAggregationService.subscribeTo(
			OmniSearchFiltersClearedEvent.EventType,
			this,
			true
		);

		this.eventAggregationService.subscribeTo(
			OmniSearchFiltersUpdatedEvent.EventType,
			this,
			true
		);

	}

	public notify(event: AnonymousStudentServiceReady |
		AnonymousStudentProfileUpdated |
		LocalizedContentServiceReadyEvent |
		CurrencyConversionServiceReadyEvent |
		OmniSearchReadyEvent |
		OmniSearchFiltersClearedEvent |
		OmniSearchFiltersUpdatedEvent |
		InlineSearchSendEvent
	): void {
		switch (event.eventType) {
		case AnonymousStudentServiceReady.EventType:
			this.handleAnonymousStudentReady(event as AnonymousStudentServiceReady);
			break;
		case AnonymousStudentProfileUpdated.EventType:
			this.handleAnonymousStudentProfileUpdate();
			break;
		case LocalizedContentServiceReadyEvent.EventType:
			this.handleLocalizedContentServiceReadyEvent(event as LocalizedContentServiceReadyEvent);
			break;
		case CurrencyConversionServiceReadyEvent.EventType:
			this.handleCurrencyConversionServiceReadyEvent(event as CurrencyConversionServiceReadyEvent);
			break;
		case OmniSearchReadyEvent.EventType:
			this.handleOmniSearchReady(event as OmniSearchReadyEvent);
			break;
		case InlineSearchSendEvent.EventType as string:
			this.handleInlineSearchSendEvent();
			break;
		case OmniSearchFiltersClearedEvent.EventType:
			this.handleOmniSearchFiltersCleared();
			break;
		case OmniSearchFiltersUpdatedEvent.EventType:
			this.handleOmniSearchFiltersUpdated();
			break;
		}

		if (this.localizedContentServiceIsReady && this.currencyConversionServiceIsReady) {
			store.mutations.setDependenciesReady(true);
		}
	}

	private handleAnonymousStudentReady(
		event: AnonymousStudentServiceReady,
	): void {
		globals.anonymousStudent = event.anonymousStudentService;
		void this.updateStudentData();
	}

	private handleAnonymousStudentProfileUpdate(): void {
		void this.updateStudentData();
	}

	private async updateStudentData(): Promise<void> {
		const data = await globals.anonymousStudent.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.CURRENCY,
			StudentField.RESIDENCE_COUNTRY_ID
		]);

		const userData: Partial<IUserData> = {};

		if (data[StudentField.NATIONALITY_COUNTRY_ID]) {
			userData.nationality = data[StudentField.NATIONALITY_COUNTRY_ID];
		}

		if (data[StudentField.CURRENCY]) {
			userData.currency = data[StudentField.CURRENCY];
		}

		if (data[StudentField.RESIDENCE_COUNTRY_ID]) {
			userData.countryOfResidence = data[StudentField.RESIDENCE_COUNTRY_ID];
		}

		store.mutations.setUserData(userData);
	}

	private handleLocalizedContentServiceReadyEvent(event: LocalizedContentServiceReadyEvent): void {
		globals.localizedContentService = event.localizedContentService;
		this.localizedContentServiceIsReady = true;
	}

	private handleCurrencyConversionServiceReadyEvent(event: CurrencyConversionServiceReadyEvent): void {
		globals.currencyConversionService = event.currencyConversionService;
		this.currencyConversionServiceIsReady = true;
	}

	private handleOmniSearchReady(event: OmniSearchReadyEvent): void {
		globals.omniSearchApplication = event.application;

		const keywordFilter = globals.omniSearchApplication.getFilter(FilterType.KEYWORD);

		if (store.getters.getText().length === 0 && keywordFilter.length > 0) {
			EventAggregationService?.instance?.publishTo(
				InlineSearchAppInitialInput.EventType,
				new InlineSearchAppInitialInput(keywordFilter[0])
			);
		}
	}

	private handleOmniSearchFiltersUpdated(): void {
		const keywordFilter = globals.omniSearchApplication.getFilter(FilterType.KEYWORD);
		if (keywordFilter.length === 0 || keywordFilter[0].length === 0){
			store.actions.clearText();
		}
	}

	private handleOmniSearchFiltersCleared(): void {
		store.actions.clearText();
	}

	private handleInlineSearchSendEvent(): void {
		void store.actions.updateSearchText();
	}
}